import { HttpClient } from '@angular/common/http';
import { Injectable, EventEmitter } from '@angular/core';
import { Observable, exhaustMap, tap, catchError, map, BehaviorSubject, from, switchMap, Subject } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { LocalStorage } from 'src/app/shared/providers/local-storage/local-storage';
import { NetworkProvider } from 'src/app/shared/providers/network/network';
import { Storage } from '@ionic/storage';
import { CrtFavorite, CrtFavoriteType } from '../../models/synthethic-view';
import { CrtHttpErrorHandlerService } from '../../providers/http-error-handler.service';
import { servicesUrl } from '../../config';

/**
 * Service Vue Synthetique
 */
@Injectable({
  providedIn: 'root',
})
export class CrtFavoriteService extends LocalStorage {
  /**
   * Clef de stockage par site
   * Exposé
   * @type {string}
   */
  public static SYNTHETIC_STORAGE_KEY = 'crt-${idSite}-synthetic-view';
  /**
   * Event pour mettre à jour la vue synthetic
   */
  public forceRefreshFavoritesInView = new Subject<void>();
  /**
   * Subject pour la mise à jours de la liste des favoris
   */
  public updateSubject$ = new BehaviorSubject<CrtFavorite[]>([]);
  /**
   * Clef de stockage par site
   * @type {string}
   */
  private readonly STORAGE_KEY = CrtFavoriteService.SYNTHETIC_STORAGE_KEY;
  /**
   * url pour la gestion des données favoris d'un site
   * @type {string}
   */
  private readonly favoriteUrl = servicesUrl.getFavoris;

  /**
   * Constructeur
   * @param {Storage} storage
   * @param {NetworkProvider} network
   * @param {HttpClient} httpClient
   * @param crtHttpErrorHandler
   */
  constructor(
    protected override storage: Storage,
    protected override network: NetworkProvider,
    private httpClient: HttpClient,
    private crtHttpErrorHandler: CrtHttpErrorHandlerService
  ) {
    super();
    this.serverRequestPeriod = 10;
  }

  /**
   * Requetes pour récuperer les données favorites à partir d'un site donnée
   * @param idSite
   * @param forceRefresh
   * @returns {Observable<SyntheticView>}
   */
  public findOrderedFavoritesByIdSite(idSite: number, forceRefresh: boolean = false): Observable<CrtFavorite[]> {
    const url = servicesUrl.getFavorisByIdSite({ idSite });
    const key = this.formatStorageKey(this.STORAGE_KEY, { idSite });

    return this.shouldUseWebservice(key, forceRefresh).pipe(
      exhaustMap((shouldUseWebservice) => {
        if (shouldUseWebservice) {
          return this.httpClient.get<CrtFavorite[]>(url).pipe(
            tap(async (values) => {
              await this.storage.set(key, values);
              await this.setLastRequestDateToNow(key);
            }),
            catchError((e) => {
              return from(this.crtHttpErrorHandler.handleError(e)).pipe(
                switchMap(() => fromPromise(this.storage.get(key)))
              );
            })
          );
        } else {
          return fromPromise(<Promise<CrtFavorite[]>>this.storage.get(key));
        }
      }),
      map((values) => this.sortCrtFavorites(values)),
      tap((values) => this.updateSubject$.next(values))
    );
  }

  /**
   * Get Favorite from storage
   * @param idSite Dk Code site
   * @param type Type du favoris
   * @param idData ID de la donnée ou du groupe
   */
  public getFavoriteFromStorage(idSite: number, type: CrtFavoriteType, idData: number): Observable<CrtFavorite> {
    // Récupération des favoris
    const scKey = this.formatStorageKey(CrtFavoriteService.SYNTHETIC_STORAGE_KEY, { idSite });

    return fromPromise(<Promise<CrtFavorite[]>>this.storage.get(scKey)).pipe(
      map((favorites) => {
        let favoriteFound: CrtFavorite;
        switch (type) {
          case CrtFavoriteType.DONNEE:
          case CrtFavoriteType.HISTO:
            favoriteFound = favorites.find((fav) => fav.idDonnee === idData);
            break;
          case CrtFavoriteType.GROUPE:
            favoriteFound = favorites.find((fav) => fav.type === type && fav.idGroupe === idData);
        }
        if (!favoriteFound) {
          throw new Error('Favorite not found in storage');
        }
        return favoriteFound;
      })
    );
  }

  /**
   * Ajout d'un favoris
   * @param favorite Favoris a ajouter
   * @param idSite dkCode du site
   */
  public addFavorite(favorite: CrtFavorite, idSite: number): Observable<CrtFavorite> {
    return this.httpClient
      .post(`${this.favoriteUrl}`, favorite, {
        responseType: 'text',
      })
      .pipe(
        map((idFavorite) => {
          return { ...favorite, identifiant: +idFavorite };
        }),
        tap(async (newFavorite) => {
          // mise à jour des données de la vue synthèse
          const scKey = this.formatStorageKey(CrtFavoriteService.SYNTHETIC_STORAGE_KEY, { idSite });
          let favorites = (await (<Promise<CrtFavorite[]>>this.storage.get(scKey))) || [];
          favorites = favorites.map((fav) => {
            const updatedFavorite = { ...fav };
            updatedFavorite.ordre += 1;
            return updatedFavorite;
          });
          favorites.push(newFavorite);

          await this.storage.set(scKey, favorites);
          this.modifyWidgetOrder(idSite, favorites).subscribe();
          this.forceRefreshFavoritesInView.next();
        })
      );
  }

  /**
   * Supression d'un favoris
   * @param favorite Favoris a supprimer
   * @param idSite Id site
   */
  public removeFavorite(favorite: CrtFavorite, idSite: number, reorderWidget = true): Observable<string> {
    return this.httpClient
      .delete(`${this.favoriteUrl}/${favorite.identifiant}`, {
        responseType: 'text',
      })
      .pipe(
        tap(async () => {
          if (reorderWidget) {
            // mise à jour des données de la vue synthèse
            const scKey = this.formatStorageKey(CrtFavoriteService.SYNTHETIC_STORAGE_KEY, { idSite });
            let favorites = (await (<Promise<CrtFavorite[]>>this.storage.get(scKey))) || [];
            favorites = favorites
              .filter(({ identifiant }) => identifiant !== favorite.identifiant)
              .sort((a, b) => a.ordre - b.ordre)
              .map((fav, index) => ({ ...fav, ordre: index + 1 }));

            this.storage.set(scKey, favorites);
            this.modifyWidgetOrder(idSite, favorites).subscribe();
            this.forceRefreshFavoritesInView.next();
          }
        })
      );
  }

  /**
   * Update favoris
   * @param idSite
   * @param favorite
   */
  public updateFavorite(idSite: number, favorite: CrtFavorite) {
    return this.httpClient
      .put(`${this.favoriteUrl}/${favorite.identifiant}`, favorite, {
        responseType: 'text',
      })
      .pipe(
        tap(async () => {
          const key = this.formatStorageKey(CrtFavoriteService.SYNTHETIC_STORAGE_KEY, { idSite });
          const favorites = (await (<Promise<CrtFavorite[]>>this.storage.get(key))) || [];
          this.storage.set(key, [
            ...favorites.filter(({ identifiant }) => identifiant !== favorite.identifiant),
            favorite,
          ]);
        })
      );
  }

  public modifyWidgetOrder(idSite: number, favorites: CrtFavorite[]): Observable<string> {
    const url = servicesUrl.getFavoris;

    return this.httpClient
      .put(url, favorites, {
        responseType: 'text',
      })
      .pipe(
        tap(async () => {
          const key = this.formatStorageKey(CrtFavoriteService.SYNTHETIC_STORAGE_KEY, { idSite });
          await this.storage.set(key, favorites);
        })
      );
  }

  /**
   * Sort favorites or return empty array
   * @param favorites
   */
  private sortCrtFavorites(favorites: CrtFavorite[]): CrtFavorite[] {
    if (!favorites) {
      return [];
    }
    return favorites.sort((a, b) => a.ordre - b.ordre);
  }
}
